/**
 *	Makes an app's generated documentation manifest available to <Document>.
 *
 *	Pass the manifest module's *namespace* (`import * as manifest from …`), not
 *	its default export: the manifest is self-accepting, so a markdown edit
 *	re-evaluates it and swaps `pages` for a new array. A namespace keeps its
 *	identity while its bindings update, so the runtime both keeps its caches and
 *	sees the new page set; a captured object would go stale on the first edit
 *	that adds or removes a page.
 */
import type { Config } from '@markdoc/markdoc';
import {
	type ComponentType,
	type PropsWithChildren,
	type ReactNode,
	createContext,
	useContext,
	useMemo,
} from 'react';

import { withBaseComponents, withBaseConfig } from '@shared/docs/markdoc';
import { type DocumentRuntime, documentRuntime } from '@shared/docs/runtime';
import type { DocumentManifest } from '@shared/docs/types';

/**
 *	Components a Markdoc `{% tag %}` can render to. Markdoc's own renderer types
 *	the map as `ComponentType<any>`; this mirrors it so a component with its own
 *	props is accepted without a cast at the call site.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DocumentComponents = Record<string, ComponentType<any>>;

interface DocumentsContextValue {
	manifest: DocumentManifest;
	runtime: DocumentRuntime;
	config: Config;
	components: DocumentComponents;
}

/**
 *	Markdoc configuration. Custom tags and nodes are per app — an app that wants
 *	`{% openapi schema="…" /%}` registers the tag here and the component it
 *	renders to in `components`. Both are laid over the renderer's baseline, see
 *	@shared/docs/markdoc.
 */
const emptyConfig: Config = { tags: {}, nodes: {}, variables: {} };
const emptyComponents: DocumentComponents = {};

const DocumentsContext =
	createContext<Optional<DocumentsContextValue>>(undefined);

interface DocumentsProviderProps extends PropsWithChildren {
	manifest: DocumentManifest;

	config?: Config;
	components?: DocumentComponents;
}

export function DocumentsProvider({
	manifest,

	config = emptyConfig,
	components = emptyComponents,

	children,
}: DocumentsProviderProps): ReactNode {
	const value = useMemo(
		() => ({
			manifest,
			runtime: documentRuntime(manifest),
			config: withBaseConfig(config),
			components: withBaseComponents(components),
		}),
		[manifest, config, components]
	);

	return (
		<DocumentsContext.Provider value={value}>
			{children}
		</DocumentsContext.Provider>
	);
}

export function useDocuments(): DocumentsContextValue {
	const context = useContext(DocumentsContext);

	if (!context) {
		throw new Error('useDocuments requires a <DocumentsProvider> ancestor.');
	}

	return context;
}
